export class HeroCarousel {
    constructor(selector, options = {}) {
        this.container = document.querySelector(selector);
        if (!this.container) return;

        this.options = Object.assign({
            interval: parseInt(this.container.dataset.interval) || 5000,
            autoPlay: true
        }, options);

        this.track = this.container.querySelector('.hero__track');
        this.backgroundActive = this.container.querySelector('.hero__background--active');
        this.backgroundNext = this.container.querySelector('.hero__background--next');
        this.items = Array.from(this.container.querySelectorAll('.hero__slide'));
        this.indicators = Array.from(this.container.querySelectorAll('.hero__indicator'));
        this.prevBtn = this.container.querySelector('.hero__arrow--prev');
        this.nextBtn = this.container.querySelector('.hero__arrow--next');
 
        this.currentIndex = 0;
        this.totalSlides = this.items.length;
        this.timerId = null;
        this.isPaused = false;
        this.scrollTimeout = null;
        this.isReordering = false;

        this.items.forEach((item, i) => {
            item.dataset.originalIndex = i;
        });
 
        this.init();
    }
 
    init() {
        this.bindEvents();
        if (this.options.autoPlay) {
            this.startAutoPlay();
        }
        // Delay color update to ensure image is loaded or at least available
        setTimeout(() => {
            this.updateColor(true); // true means initial load, no transition
            this.initCountdowns();
        }, 100);
    }
 
    bindEvents() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => {
                this.slidePrev();
                this.resetAutoPlay();
            });
        }
 
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => {
                this.slideNext();
                this.resetAutoPlay();
            });
        }
 
        this.indicators.forEach((indicator, index) => {
            indicator.addEventListener('click', () => {
                this.goToOriginalIndex(index);
                this.resetAutoPlay();
            });
        });
 
        if (this.track) {
            this.track.addEventListener('scroll', () => this.onScrollDebounced());
        }
 
        this.container.addEventListener('mouseenter', () => this.pauseTimer());
        this.container.addEventListener('mouseleave', () => this.resumeTimer());
        this.container.addEventListener('focusin', () => this.pauseTimer());
        this.container.addEventListener('focusout', () => this.resumeTimer());
        
        // Handle images load for color extraction
        this.items.forEach((slide, index) => {
            const img = slide.querySelector('img');
            if (img) {
                if (img.complete) {
                     // already loaded
                } else {
                    img.addEventListener('load', () => {
                        if (index === this.currentIndex) {
                            this.updateColor();
                        }
                    });
                }
            }
        });
    }

    /**
     * Slide to the next item. Scrolls right by one slide width.
     * After scroll settles, the passed slide is moved to the end of the DOM.
     */
    slideNext() {
        if (!this.track || this.totalSlides <= 1 || this.isReordering) return;

        const slideWidth = this.track.offsetWidth;
        this.track.scrollTo({
            left: slideWidth,
            behavior: 'smooth'
        });
    }

    slidePrev() {
        if (!this.track || this.totalSlides <= 1 || this.isReordering) return;

        this.isReordering = true;

        const lastSlide = this.track.lastElementChild;
        this.track.insertBefore(lastSlide, this.track.firstElementChild);

        const slideWidth = this.track.offsetWidth;
        this.setInstantScroll(slideWidth);

        requestAnimationFrame(() => {
            this.isReordering = false;
            this.track.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
        });
    }

    goToOriginalIndex(targetOriginalIndex) {
        if (!this.track || this.isReordering) return;

        const currentOriginalIndex = parseInt(this.track.children[0].dataset.originalIndex, 10);
        if (targetOriginalIndex === currentOriginalIndex) return;

        const children = Array.from(this.track.children);
        const targetDomIndex = children.findIndex(
            c => parseInt(c.dataset.originalIndex, 10) === targetOriginalIndex
        );

        if (targetDomIndex === -1) return;

        const forwardSteps = targetDomIndex;
        const backwardSteps = this.totalSlides - targetDomIndex;

        if (forwardSteps <= backwardSteps) {
            const slideWidth = this.track.offsetWidth;
            this.track.scrollTo({
                left: slideWidth * forwardSteps,
                behavior: 'smooth'
            });
        } else {
            this.isReordering = true;

            for (let i = 0; i < backwardSteps; i++) {
                this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
            }

            const slideWidth = this.track.offsetWidth;
            this.setInstantScroll(backwardSteps * slideWidth);

            requestAnimationFrame(() => {
                this.isReordering = false;
                this.track.scrollTo({
                    left: 0,
                    behavior: 'smooth'
                });
            });
        }
    }

    setInstantScroll(scrollLeft) {
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = scrollLeft;
        void this.track.offsetHeight; // force reflow
        this.track.style.scrollSnapType = '';
        this.track.style.scrollBehavior = '';
    }

    onScrollDebounced() {
        if (this.isReordering) return;
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => this.onScrollSettled(), 100);
    }

    /**
     * Called after scroll animation settles.
     * Reorders DOM so the current slide is at position 0 and resets scrollLeft.
     */
    onScrollSettled() {
        if (!this.track || this.isReordering) return;

        const slideWidth = this.track.offsetWidth;
        const scrollLeft = this.track.scrollLeft;
        const position = Math.round(scrollLeft / slideWidth);

        if (position > 0 && position < this.totalSlides) {
            this.isReordering = true;

            for (let i = 0; i < position; i++) {
                this.track.appendChild(this.track.firstElementChild);
            }

            this.setInstantScroll(0);

            requestAnimationFrame(() => {
                this.isReordering = false;
            });
        }

        const firstSlide = this.track.children[0];
        if (firstSlide) {
            const newIndex = parseInt(firstSlide.dataset.originalIndex, 10);
            if (newIndex !== this.currentIndex) {
                this.currentIndex = newIndex;
                this.updateIndicators();
                this.updateColor();
            }
        }
    }
 
    startAutoPlay() {
        this.stopAutoPlay();
        if (this.options.interval > 0 && this.totalSlides > 1) {
            this.timerId = setInterval(() => {
                if (!this.isPaused) {
                    this.slideNext();
                }
            }, this.options.interval);
        }
    }
 
    stopAutoPlay() {
        if (this.timerId) clearInterval(this.timerId);
    }
 
    resetAutoPlay() {
        this.stopAutoPlay();
        this.startAutoPlay();
    }
 
    pauseTimer() {
        this.isPaused = true;
        this.stopAutoPlay();
    }
 
    resumeTimer() {
        this.isPaused = false;
        this.startAutoPlay();
    }
 
    updateIndicators() {
        this.items.forEach((item) => {
            const originalIndex = parseInt(item.dataset.originalIndex, 10);
            const isCurrent = originalIndex === this.currentIndex;
            const link = item.querySelector('.hero__link');

            if (isCurrent) {
                item.setAttribute('aria-hidden', 'false');
                if (link) link.setAttribute('tabindex', '0');
            } else {
                item.setAttribute('aria-hidden', 'true');
                if (link) link.setAttribute('tabindex', '-1');
            }
        });
 
        this.indicators.forEach((indicator, index) => {
            if (index === this.currentIndex) {
                indicator.classList.add('hero__indicator--active');
                indicator.setAttribute('aria-selected', 'true');
                indicator.setAttribute('tabindex', '0');
            } else {
                indicator.classList.remove('hero__indicator--active');
                indicator.setAttribute('aria-selected', 'false');
                indicator.setAttribute('tabindex', '-1');
            }
        });
    }
 
    updateColor(immediate = false) {
        const currentSlide = Array.from(this.track.children).find(
            c => parseInt(c.dataset.originalIndex, 10) === this.currentIndex
        );

        if (!currentSlide || !this.backgroundActive || !this.backgroundNext) return;
        
        const img = currentSlide.querySelector('img');
        if (img) {
            const nextBg = this.backgroundNext;
            const activeBg = this.backgroundActive;
            const resolvedSrc = img.currentSrc || img.src;

            if (immediate) {
                // On initial load, the active background already has the style from HTML.
                // We just ensure the CSS variable is set correctly with resolvedSrc.
                if (resolvedSrc) {
                    activeBg.style.setProperty('--hero-bg-image', `url("${resolvedSrc}")`);
                }
                return;
            }

            // Control the image source via CSS variable instead of inline border properties
            // This allows SCSS media queries to selectively apply border-image on mobile 
            // and background-image on desktop.
            nextBg.style.setProperty('--hero-bg-image', `url("${resolvedSrc}")`);
            
            // Smooth Crossfade
            activeBg.style.opacity = 0;
            nextBg.style.opacity = 1;

            // Swap references for the next time
            this.backgroundActive = nextBg;
            this.backgroundNext = activeBg;
        }
    }


    initCountdowns() {
        const countdowns = this.container.querySelectorAll('.hero__countdown');
        if (!countdowns.length) return;

        countdowns.forEach(countdown => {
            const endDateStr = countdown.getAttribute('data-end-date');
            if (!endDateStr) return;

            // Safer date parsing for cross-browser compatibility
            const parsedDateStr = endDateStr.replace(/-/g, '/').replace('T', ' ');
            const endDate = new Date(parsedDateStr).getTime();
            
            if (isNaN(endDate)) {
                console.error('Invalid date format:', endDateStr);
                return;
            }

            const daysEl = countdown.querySelector('.hero__countdown-days');
            const hoursEl = countdown.querySelector('.hero__countdown-hours');
            const minutesEl = countdown.querySelector('.hero__countdown-minutes');
            const secondsEl = countdown.querySelector('.hero__countdown-seconds');
            
            const items = Array.from(countdown.querySelectorAll('.hero__countdown-item'));
            const separators = Array.from(countdown.querySelectorAll('.hero__countdown-separator'));

            const updateCountdown = () => {
                const now = new Date().getTime();
                const distance = endDate - now;

                if (isNaN(distance) || distance < 0) {
                    countdown.style.display = 'none';
                    return;
                }

                const days = Math.floor(distance / (1000 * 60 * 60 * 24));
                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                // Text values are updated for both mobile and desktop
                if (daysEl) daysEl.innerText = days.toString().padStart(2, '0');
                if (hoursEl) hoursEl.innerText = hours.toString().padStart(2, '0');
                if (minutesEl) minutesEl.innerText = minutes.toString().padStart(2, '0');
                if (secondsEl) secondsEl.innerText = seconds.toString().padStart(2, '0');

                // Start by filtering out "higher" units that are zero (e.g., if days=0, don't show days)
                let visibleItems = [
                    { el: countdown.querySelector('.hero__countdown-days')?.parentElement, val: days },
                    { el: countdown.querySelector('.hero__countdown-hours')?.parentElement, val: hours },
                    { el: countdown.querySelector('.hero__countdown-minutes')?.parentElement, val: minutes },
                    { el: countdown.querySelector('.hero__countdown-seconds')?.parentElement, val: seconds }
                ].filter(item => item.el);

                // We always want to show at least one unit even if it's 0 (like seconds)
                let startedShowing = false;
                visibleItems.forEach((item, index) => {
                    if (item.val > 0 || startedShowing || index === visibleItems.length - 1) {
                        item.el.style.display = 'flex';
                        startedShowing = true;
                    } else {
                        item.el.style.display = 'none';
                    }
                });

                // Helper to update separators based on visible items
                const updateSeparators = () => {
                    separators.forEach(sep => sep.style.display = 'none');
                    let lastVisibleItem = null;
                    
                    items.forEach(item => {
                        if (item.style.display !== 'none') {
                            if (lastVisibleItem) {
                              const sep = lastVisibleItem.nextElementSibling;
                              if(sep && sep.classList.contains('hero__countdown-separator')) {
                                  sep.style.display = 'block';
                              }
                            }
                            lastVisibleItem = item;
                        }
                    });
                };

                updateSeparators();
            };

            updateCountdown();
            setInterval(updateCountdown, 1000);
        });
    }
}
